import { useCallback, useEffect, useRef, useState } from 'react';
import { marksApi } from '../lib/masteryApi';
import type { MarkCell, MarkColumn, MarkSheet } from '../lib/masteryApi';

/**
 * ENTERING MARKS, ONE QUESTION AT A TIME, FAST ENOUGH THAT SOMEBODY WILL ACTUALLY DO IT.
 *
 * ⚠ THE SPEED IS THE FEATURE. Thirty-nine questions × fifty-two children is two thousand numbers.
 * A teacher will not enter two thousand numbers into a form with a Save button at the bottom, and
 * a chapter-level diagnosis built on marks nobody enters is a diagnosis of nothing. So: one row per
 * child, questions across, the cursor moves on its own, and the row saves when you leave it.
 *
 * ⚠ AN EMPTY BOX IS NOT ZERO. A blank cell means "not marked yet" and a dash means "the child did
 * not attempt it" — two different things, and both different from a nought. Typing `-` records not
 * attempted; that is the fastest key on the board for the commonest case, because internal choice
 * means every child leaves several questions untouched by design.
 *
 * ⚠ SAVING IS PER CHILD, ON LEAVING THE ROW. Saving per keystroke is a request a school's wifi
 * drops halfway through a script; saving once at the end is a teacher losing forty minutes.
 */
export function MarkEntryPage({ paperId }: { paperId?: string }) {
  const [sheet, setSheet] = useState<MarkSheet | null>(null);
  const [classId, setClassId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // ⚠ The grid is held in a ref as well as in state. State drives the render; the ref is what the
  // save reads, because a blur handler firing during a React batch can otherwise read a value one
  // keystroke old — and one keystroke old, on the last cell of a row, is a lost mark.
  const grid = useRef<Map<string, Map<string, MarkCell>>>(new Map());

  useEffect(() => {
    if (!paperId) { setLoading(false); return; }
    let alive = true;
    marksApi
      .sheet(paperId, classId)
      .then((s) => {
        if (!alive) return;
        setSheet(s);
        setFailed(false);
        if (!classId && s.academicClassId) setClassId(s.academicClassId);

        const m = new Map<string, Map<string, MarkCell>>();
        for (const r of s.rows) m.set(r.studentId, new Map(r.cells.map((c) => [c.itemId, c])));
        grid.current = m;
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [paperId, classId]);

  const set = useCallback((studentId: string, col: MarkColumn, raw: string) => {
    const row = grid.current.get(studentId) ?? new Map<string, MarkCell>();
    grid.current.set(studentId, row);

    const t = raw.trim();

    if (t === '') { row.delete(col.itemId); }
    else if (t === '-' || t.toLowerCase() === 'x') {
      // Not attempted. ⚠ Zero marks, but recorded as no evidence rather than as a wrong answer.
      row.set(col.itemId, { itemId: col.itemId, marks: 0, attempted: false });
    } else if (col.correctOption && /^[a-dA-D]$/.test(t)) {
      // ⚠ THE MCQ FAST PATH. A whole twenty-question section is marked by typing the letters the
      // child circled — the software knows which one is right. This is the single biggest saving
      // on the screen and the reason Section A stops being forty minutes of arithmetic.
      const correct = t.toUpperCase() === col.correctOption.toUpperCase();
      row.set(col.itemId, { itemId: col.itemId, marks: correct ? col.marks : 0, attempted: true });
    } else {
      const n = Number(t);
      if (!Number.isFinite(n)) return;
      // Clamped here and again in the domain. A teacher typing 55 for 5 must not be able to
      // poison a class's unit percentages from a keyboard.
      row.set(col.itemId, {
        itemId: col.itemId,
        marks: Math.max(0, Math.min(col.marks, n)),
        attempted: true,
      });
    }

    setDirty((d) => new Set(d).add(studentId));
  }, []);

  const saveRow = useCallback((studentId: string) => {
    if (!paperId || !dirty.has(studentId)) return;
    const cells = [...(grid.current.get(studentId)?.values() ?? [])];
    setSaving(studentId);
    marksApi
      .save(paperId, studentId, cells)
      .then(() => {
        setDirty((d) => { const n = new Set(d); n.delete(studentId); return n; });
      })
      .catch(() => setFlash('That row did not save. Check the connection before you carry on.'))
      .finally(() => setSaving(null));
  }, [paperId, dirty]);

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Opening the mark sheet…</p>;
  if (failed || !sheet) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the mark sheet.</div>;
  }

  const value = (studentId: string, col: MarkColumn): string => {
    const c = grid.current.get(studentId)?.get(col.itemId);
    if (!c) return '';
    if (!c.attempted) return '-';
    return String(c.marks);
  };

  /**
   * ⚠ THE TOTAL HAS TO MOVE WHILE THE TEACHER TYPES, and it did not.
   *
   * The cell is uncontrolled on purpose — typing "B" leaves "B" in the box, which is the right
   * echo: the teacher entered what the child circled, and rewriting it to "1" under their cursor
   * would fight them. But the row's Total came from the SERVER'S snapshot and never changed, so
   * marking a twenty-question multiple-choice section produced no visible confirmation of anything
   * at all — no cell changed, no total moved, and the only feedback in the whole interaction was
   * the words "1 row(s) not saved" appearing above the grid.
   *
   * On the one screen whose entire argument is that a teacher will actually use it, that is the
   * difference between confidence and typing into a void. The total is now computed from the grid,
   * so a correct letter visibly adds its mark and a dash visibly adds nothing.
   *
   * ⚠ IT READS THE REF, NOT STATE, for the same reason `saveRow` does — a value one keystroke old
   * on the last cell of a row is a lost mark. `set` calls setDirty on every change, so the render
   * that shows the new total always follows the keystroke that caused it.
   */
  const liveTotal = (studentId: string, fallback: number): number => {
    const row = grid.current.get(studentId);
    if (!row) return fallback;
    let n = 0;
    for (const c of row.values()) n += c.marks;
    return n;
  };

  return (
    <div className="mas">
      <header className="mas-head">
        <h1>Enter marks</h1>
        <p className="mas-sub">
          {sheet.paperTitle} · {sheet.subjectName} · out of {sheet.totalMarks}. Type a mark, or the
          letter the child circled for a multiple-choice question, or <strong>−</strong> for a
          question they did not attempt. The row saves when you leave it.
        </p>
      </header>

      {sheet.note && <div className="mas-note">{sheet.note}</div>}
      {flash && <div className="mas-bad">{flash}</div>}

      <div className="card mas-controls">
        <label>
          <span>Class</span>
          <select value={classId ?? ''} onChange={(e) => { setClassId(e.target.value); setLoading(true); }}>
            {sheet.classes.map((c) => (
              <option key={c.id} value={c.id}>{c.label} ({c.students})</option>
            ))}
          </select>
        </label>
        <span className="mas-progress">
          {sheet.captured} of {sheet.expected} marks captured
          {dirty.size > 0 && <em> · {dirty.size} row(s) not saved</em>}
        </span>
      </div>

      {/* ⚠ The WRAPPER scrolls, never the page. Thirty-nine columns will not fit on any screen and
          a document that scrolls sideways is unusable on a phone. */}
      <div className="card mas-gridwrap">
        <table className="mas-grid">
          <thead>
            <tr>
              <th className="sticky-name">Child</th>
              {sheet.columns.map((c) => (
                <th key={c.itemId} title={`${c.preview}${c.unitName ? ` — ${c.unitName}` : ''}`}>
                  <span className="mas-col-num">
                    {c.sectionLabel}{c.number}{c.isAlternative ? '·or' : ''}
                  </span>
                  <span className="mas-col-marks">{c.marks}</span>
                </th>
              ))}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((r) => (
              <tr key={r.studentId} className={dirty.has(r.studentId) ? 'is-dirty' : ''}>
                <td className="sticky-name">
                  <span className="mas-rowname">{r.fullName}</span>
                  <span className="mas-rowadm">{r.admissionNumber}</span>
                  {saving === r.studentId && <span className="mas-saving">saving…</span>}
                </td>
                {sheet.columns.map((c) => (
                  <td key={c.itemId}>
                    <input
                      className="mas-cell"
                      defaultValue={value(r.studentId, c)}
                      onChange={(e) => set(r.studentId, c, e.target.value)}
                      onBlur={(e) => {
                        // Leaving the LAST cell of a row is what commits it. Moving within the
                        // row must not fire a request per cell.
                        const next = e.relatedTarget as HTMLElement | null;
                        if (!next?.closest(`[data-row="${r.studentId}"]`)) saveRow(r.studentId);
                      }}
                      data-row={r.studentId}
                      inputMode={c.correctOption ? 'text' : 'decimal'}
                      aria-label={`${r.fullName}, question ${c.sectionLabel}${c.number}`}
                    />
                  </td>
                ))}
                <td className="num mas-total">{liveTotal(r.studentId, r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mas-sub">
        Once every script is in, the mastery report turns these numbers into which chapter each
        child does not understand — and which chapter the whole class needs taught again.
      </p>
    </div>
  );
}
